import mixpanel from 'mixpanel-browser';

import { BuildADashTrackingEvents } from '@revelio/core';

import { SelectionCategoryLabelOverrides } from '../../shared/selection-lists/selection-category-label-overrides';
import type { ReportPageConfig } from '../CustomDash/components/PageConfigProvider/types';
import type { IReportEntityMap } from '../reports/report-builder/02-entity-configuration/mw-reports/utils';
import { reportBuilderStore } from '../reports/report-builder/report-builder.repository';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../reports/utils/report-builder-paths';
import {
  type GlobalAssistantReportParamSuggestion,
  type GlobalAssistantReportPrefillWarning,
  postGlobalAssistantReportParams,
} from './global-ai-assistant-api';
import { globalAssistantReportLaunchPendingStore } from './global-ai-assistant-report-launch-pending';
import { countResolvedSuggestions } from './global-ai-assistant-report-param-mapping';
import { applyGlobalAssistantReportPrefill } from './global-ai-assistant-report-prefill';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

export type ReportOfferTranscriptMessage = Extract<
  GlobalAiTranscriptMessage,
  { kind: 'report_offer' }
>;

type RoleTaxonomy = {
  rolesTax: string;
  skillsTax: number;
};

type FetchReportOfferParamsResult =
  | {
      ok: true;
      suggestions: GlobalAssistantReportParamSuggestion[];
    }
  | {
      ok: false;
      errorMessage: string;
    };

const toPrefillErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'unknown';

const toTitleCaseFromSnake = (value: string): string =>
  value
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatSelectionLevelForWarning = (levelId: string): string => {
  const overrideLabel = SelectionCategoryLabelOverrides[levelId]?.trim();
  if (overrideLabel) {
    const normalizedOverride = overrideLabel.toUpperCase();
    if (levelId.startsWith('role_')) {
      return `Role ${normalizedOverride}`;
    }
    if (levelId.startsWith('skill')) {
      return `Skill ${normalizedOverride}`;
    }
    return overrideLabel;
  }

  if (levelId.startsWith('role_')) {
    const roleSuffix = levelId.replace('role_', '').toUpperCase();
    return `Role ${roleSuffix}`;
  }

  if (levelId.startsWith('skill')) {
    const skillSuffix = levelId.replace('skill_', '').toUpperCase();
    return `Skill ${skillSuffix}`;
  }

  return toTitleCaseFromSnake(levelId);
};

export const buildReportOfferPrefillErrorText = (
  reportName: string,
  errorMessage: string
): string =>
  `We couldn't extract filters for the ${reportName} report (${errorMessage}). You can still configure the report manually in the builder.`;

export const buildReportOfferPrefillWarningText = (
  warning: GlobalAssistantReportPrefillWarning
): string => {
  const foundLevel = formatSelectionLevelForWarning(warning.foundLevelId);
  const supportedLevels = warning.supportedLevelIds
    .map((levelId) => formatSelectionLevelForWarning(levelId))
    .join(', ');
  return `We found "${warning.selectionName}" for ${warning.parameterDisplayName}, but it is in ${foundLevel} and this report supports ${supportedLevels}. Please select this value manually.`;
};

const trackPromotedPrefillMetrics = (
  reportType: string,
  suggestions: GlobalAssistantReportParamSuggestion[]
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: suggestions.length,
    global_assistant_report_prefill_resolved:
      countResolvedSuggestions(suggestions),
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped:
      suggestions.length - countResolvedSuggestions(suggestions),
  });
};

const trackPromotedPrefillEmpty = (reportType: string): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: 0,
    global_assistant_report_prefill_resolved: 0,
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped: 0,
    global_assistant_report_prefill_empty: true,
  });
};

const trackLegacyPrefillMetrics = (
  reportType: string,
  prefillResult: {
    extractedCount: number;
    resolvedCount: number;
    appliedCount: number;
    skippedCount: number;
  }
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: prefillResult.extractedCount,
    global_assistant_report_prefill_resolved: prefillResult.resolvedCount,
    global_assistant_report_prefill_applied: prefillResult.appliedCount,
    global_assistant_report_prefill_skipped: prefillResult.skippedCount,
  });
};

const trackPrefillFailure = (
  reportType: string,
  errorMessage: string
): void => {
  mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_PREFILL, {
    global_assistant_report_type: reportType,
    global_assistant_report_prefill_extracted: 0,
    global_assistant_report_prefill_resolved: 0,
    global_assistant_report_prefill_applied: 0,
    global_assistant_report_prefill_skipped: 0,
    global_assistant_report_prefill_error: errorMessage,
  });
};

export const fetchReportOfferParamSuggestions = async (params: {
  promptForParams: string;
  reportType: string;
  roleTaxonomy: RoleTaxonomy;
}): Promise<FetchReportOfferParamsResult> => {
  try {
    const suggestions = await postGlobalAssistantReportParams({
      prompt: params.promptForParams,
      reportType: params.reportType,
      rolesTax: params.roleTaxonomy.rolesTax,
      skillsTax: params.roleTaxonomy.skillsTax,
    });
    return { ok: true, suggestions };
  } catch (prefillError) {
    return {
      ok: false,
      errorMessage: toPrefillErrorMessage(prefillError),
    };
  }
};

const navigateToReportEntityStep = (
  navigate: (
    to: string,
    options?: { replace?: boolean; state?: { skipGuard?: boolean } }
  ) => void,
  skipGuard: boolean
): void => {
  const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
  const navigationOptions = skipGuard
    ? { replace: true, state: { skipGuard: true } }
    : { replace: true };

  navigate(entityPath, navigationOptions);
};

const releasePromotedLaunchHandoffIfNoPrefill = (): void => {
  queueMicrotask(() => {
    const { promotedTemplateId, paramSuggestions } =
      globalAssistantReportLaunchPendingStore.getState();
    if (promotedTemplateId !== null && paramSuggestions.length === 0) {
      globalAssistantReportLaunchPendingStore
        .getState()
        .actions.clearPromotedLaunch();
    }
  });
};

export const launchReportFromOffer = async (params: {
  msg: ReportOfferTranscriptMessage;
  promptForParams: string;
  roleTaxonomy: RoleTaxonomy;
  promotedTemplate: ReportPageConfig | null;
  reportEntityMap: IReportEntityMap;
  pathname: string;
  navigate: (
    to: string,
    options?: { replace?: boolean; state?: { skipGuard?: boolean } }
  ) => void;
  skipGuard?: boolean;
  onPrefillError: (text: string) => void;
  onPrefillWarning?: (text: string) => void;
}): Promise<void> => {
  const {
    msg,
    promptForParams,
    roleTaxonomy,
    promotedTemplate,
    reportEntityMap,
  } = params;

  if (promotedTemplate) {
    reportBuilderStore.actions.setReports([]);

    globalAssistantReportLaunchPendingStore.actions.setPromotedLaunch({
      promotedTemplateId: promotedTemplate.id,
      reportType: msg.reportType,
      paramSuggestions: [],
    });

    if (promptForParams) {
      const paramResult = await fetchReportOfferParamSuggestions({
        promptForParams,
        reportType: msg.reportType,
        roleTaxonomy,
      });
      if (paramResult.ok) {
        globalAssistantReportLaunchPendingStore.actions.updatePromotedLaunchSuggestions(
          paramResult.suggestions
        );
        if (paramResult.suggestions.length > 0) {
          trackPromotedPrefillMetrics(msg.reportType, paramResult.suggestions);
        } else {
          trackPromotedPrefillEmpty(msg.reportType);
        }
      } else {
        trackPrefillFailure(msg.reportType, paramResult.errorMessage);
        params.onPrefillError(
          buildReportOfferPrefillErrorText(
            msg.reportName,
            paramResult.errorMessage
          )
        );
      }

      navigateToReportEntityStep(params.navigate, params.skipGuard ?? false);
      if (paramResult.ok && paramResult.suggestions.length === 0) {
        releasePromotedLaunchHandoffIfNoPrefill();
      }
      return;
    }

    navigateToReportEntityStep(params.navigate, params.skipGuard ?? false);
    releasePromotedLaunchHandoffIfNoPrefill();
    return;
  }

  globalAssistantReportLaunchPendingStore
    .getState()
    .actions.clearPromotedLaunch();

  const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
  if (globalAssistantPathsMatch(params.pathname, entityPath)) {
    globalAssistantReportLaunchPendingStore
      .getState()
      .actions.bumpMwReportLaunchSeq();
  }

  reportBuilderStore.actions.setReports([
    {
      id: 0,
      reportType: msg.reportType,
      selections: {},
    },
  ]);

  if (promptForParams) {
    const paramResult = await fetchReportOfferParamSuggestions({
      promptForParams,
      reportType: msg.reportType,
      roleTaxonomy,
    });
    if (paramResult.ok) {
      globalAssistantReportLaunchPendingStore.actions.setMwReportPrefill({
        reportType: msg.reportType,
        paramSuggestions: paramResult.suggestions,
      });
      const prefillResult = await applyGlobalAssistantReportPrefill({
        reportId: 0,
        reportType: msg.reportType,
        suggestions: paramResult.suggestions,
        reportEntityMap,
      });
      trackLegacyPrefillMetrics(msg.reportType, prefillResult);
      prefillResult.warnings?.forEach((warning) => {
        params.onPrefillWarning?.(buildReportOfferPrefillWarningText(warning));
      });
    } else {
      trackPrefillFailure(msg.reportType, paramResult.errorMessage);
      params.onPrefillError(
        buildReportOfferPrefillErrorText(
          msg.reportName,
          paramResult.errorMessage
        )
      );
    }
  }

  navigateToReportEntityStep(params.navigate, params.skipGuard ?? false);
};
